import { t9n } from 't9n/t9n'

import { CompetitionPayload } from 'models/endpoints-payload'
import { InputElement } from '../models/input-form'

import { emailValidator, emptyValidator, checkedValidator } from 'services/validators-functions'
import competitionSubscribe from 'services/competition-subscribe'
import { checkEmailAvailability } from 'services/check-email-availability'
import { signUpForNewsletter } from 'services/newsletter'

const $submitFormButton = document.querySelector<HTMLButtonElement>('[data-submit-button]')
const $emailInputElement = document.querySelector<HTMLInputElement>("input[name='email']")
const $serviceTermsInputElement = document.querySelector<HTMLInputElement>("input[name='reg-consent']")
const $promotionTermsInputElement = document.querySelector<HTMLInputElement>("input[name='prom-consent']")
const $marketingConsentInputElement = document.querySelector<HTMLInputElement>("input[name='marketing-consent']")
const $eventInputElement = document.querySelector<HTMLInputElement>("input[name='competition']")
const $answerElement = document.querySelector<HTMLTextAreaElement>("textarea[name='answer']")
const $allErrorSpans = document.querySelectorAll<HTMLElement>('[data-error]')
const $allErrorCheckboxSpans = document.querySelectorAll<HTMLElement>('[data-checkbox-error]')

const getEmailFromForm = (): string => $emailInputElement?.value.trim()
const getServiceTermConsent = (): boolean => ($serviceTermsInputElement ? $serviceTermsInputElement.checked : true)
const getPromotionTermConsent = (): boolean =>
  $promotionTermsInputElement ? $promotionTermsInputElement?.checked : true
const getMarketingConsent = (): boolean =>
  $marketingConsentInputElement ? $marketingConsentInputElement.checked : false
const getAnswerFromForm = (): string => $answerElement?.value.trim()
const getEvent = (): string => $eventInputElement?.value.trim()

const showCheckboxError = (errorID: string): void => {
  const $errorElement = document.querySelector(`[data-checkbox-error='${errorID}']`) as HTMLElement
  if ($errorElement) $errorElement.hidden = false
}

const showError = ($element: InputElement, errorID: string): void => {
  const $errorElement = document.querySelector(`[data-error='${errorID}']`) as HTMLElement
  if ($element) $element.classList.add('is-error')
  if ($errorElement) $errorElement.hidden = false
}

const resetAllErrors = () => {
  $emailInputElement?.classList.remove('is-error')
  $answerElement?.classList.remove('is-error')

  for (const $singleErrorSpan of $allErrorSpans) {
    $singleErrorSpan.hidden = true
  }

  for (const $singleErrorCheckboxSpan of $allErrorCheckboxSpans) {
    $singleErrorCheckboxSpan.hidden = true
  }
}

const checkIsEmailRegistered = async (email: string): Promise<boolean | null> => {
  try {
    const emailResponse = await checkEmailAvailability({ email })

    if ('available' in emailResponse) return !emailResponse.available
    return null
  } catch (error: unknown) {
    return null
  }
}

const checkEmailValidation = async (emailMustBeRegistered: boolean): Promise<boolean> => {
  const email = getEmailFromForm()

  if (emptyValidator(email)) {
    showError($emailInputElement, 'email-mandatory')
    return false
  }

  if (emailValidator(email)) {
    showError($emailInputElement, 'email-invalid')
    return false
  }

  if (emailMustBeRegistered) {
    const isEmailRegistered = await checkIsEmailRegistered(email)
    if (!isEmailRegistered) {
      showError($emailInputElement, 'email-not-exist')
      return false
    }
  }

  return true
}

const checkAnswerValidation = (): boolean =>
  emptyValidator(getAnswerFromForm()) ? (showError($answerElement, 'answer-mandatory'), false) : true
const checkTermsOfServiceValidation = (): boolean =>
  checkedValidator(getServiceTermConsent()) ? (showCheckboxError('reg-consent-mandatory'), false) : true
const checkPromotionTermsValidation = (): boolean =>
  checkedValidator(getPromotionTermConsent()) ? (showCheckboxError('prom-consent-mandatory'), false) : true

const isFormComplete = async (emailMustBeRegistered: boolean, answerOptional: boolean): Promise<boolean> => {
  return [
    await checkEmailValidation(emailMustBeRegistered),
    answerOptional || checkAnswerValidation(),
    checkTermsOfServiceValidation(),
    checkPromotionTermsValidation(),
  ].every((v) => v)
}

const successSubmit = (): void => {
  const $formContent = document.querySelector<HTMLDivElement>('[data-form-content]')
  const $formTitle = document.querySelector<HTMLHeadingElement>('[data-form-title]')
  const $formSubTitle = document.querySelector<HTMLHeadingElement>('[data-form-subtitle]')
  const $submitButton = document.querySelector<HTMLButtonElement>('[data-submit-button]')

  if ($formContent) $formContent.hidden = true
  if ($formSubTitle) $formSubTitle.hidden = true
  if ($formTitle) $formTitle.textContent = t9n('landingForm.competitionFormThanks')
  if ($submitButton) $submitButton.hidden = true
}

const handleFormSubmit = async (emailMustBeRegistered: boolean, answerOptional: boolean): Promise<void> => {
  resetAllErrors()
  if (await isFormComplete(emailMustBeRegistered, answerOptional)) {
    const payload: CompetitionPayload = {
      email: getEmailFromForm(),
      event: getEvent(),
      date: new Date().toISOString(),
      terms_and_conditions_accepted: getPromotionTermConsent(),
      ...(answerOptional ? {} : { answer: getAnswerFromForm() }),
    }

    competitionSubscribe(payload)
      .then(() => {
        if (getMarketingConsent()) {
          signUpForNewsletter({ email: getEmailFromForm() })
        }
        successSubmit()
      })
      .catch((e) => {
        const JSONResponse = JSON.parse(e.responseText)

        if (JSONResponse.error) {
          showError(null, 'general-invalid')
        }
      })
  }
}

export function handleCompetitionForm(emailMustBeRegistered: boolean = false, answerOptional: boolean = false): void {
  $submitFormButton.addEventListener('click', () => handleFormSubmit(emailMustBeRegistered, answerOptional))
}
